/// <amd-module name="Reporting/ReportWidgetHost/ReportWidgetHost" />
import { WidgetSize } from "Dashboards/Grid/WidgetSizeConverter";
import { DashboardWidgetProps, SemanticVersion, WidgetGridSize } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WidgetCopyProps } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";
import { WidgetPresentationMode, WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import { WidgetHost } from "Dashboards/WidgetHost/WidgetHost";
import * as React from "react";
import { copyWidget } from "Reporting/ReportWidgetHost/CopyWidget";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { VssComponent } from "VSS/Platform/Layout";
import { Spinner } from "VSSUI/Spinner";

export { WidgetGridSize, SemanticVersion };

/** Covers WidgetProps plus some supplemental details.
 * Render and Pinning Sizes are not currently an option, as all the reports use shared render-size logic.
 * This can be exposed once req's vary.
 */
export interface ReportWidgetHostProps {
    contributionId: string;
    name: string;
    settings: string;
    settingsVersion?: SemanticVersion;

    /**
     * The name of the page for widget telemetry to track usage.
     */
    parentPageName: string;

    /** The desired render size of the widget, in pixels. */
    renderSize: WidgetSize;
    /** The dashboard grid-size of the widget when it is pinned to a dashboard. */
    widgetPinningSize: WidgetGridSize;

    /** An opt-out to block pinning of particular widget types which won't work on the dashboard. */
    allowPinning?: boolean;

    teamContext: WidgetTeamContext;

    /** Allows forced reload of widget, for scenarios where settings have not changed. */
    lastRefreshDate?: Date;
}

/** This handles routine layout/decoration concerns around a Report Widget Host */
export class ReportWidgetHostContainer extends VssComponent<ReportWidgetHostProps> {
    public render(): JSX.Element | null {
        return (
            <div className="report-widget-host-container flex-column">
                <ReportWidgetHost {...this.props} />
            </div>
        );
    }
}

export interface ReportHostState {
    isLoading: boolean;
    iterationKey: number;
}

/** A pixel-sized widget Host for use in single-visual report scenarios.
 * Note: This Component only supports New Platform Widgets. Legacy widgets which are strictly sized to a dashboard grid will have rendering issues, if used here.
 */
export class ReportWidgetHost extends VssComponent<ReportWidgetHostProps, ReportHostState> {
    constructor(props: ReportWidgetHostProps) {
        super(props);
        this.state = { isLoading: true, iterationKey: 0 };
    }

    public componentWillReceiveProps(newProps: ReportWidgetHostProps) {
        if (newProps.settings !== this.props.settings || newProps.lastRefreshDate !== this.props.lastRefreshDate) {
            this.setState({ isLoading: true, iterationKey: this.state.iterationKey + 1 });
        }
    }

    public render(): JSX.Element | null {
        const widgetProps = this.assembleWidgetProps(this.props);
        const allowPinning = this.props.allowPinning;
        return (
            <>
                {this.state.isLoading && (
                    <div style={this.props.renderSize}>
                        <Spinner className="report-host-spinner" />{" "}
                    </div>
                )}
                <div className={this.state.isLoading ? "hidden" : ""}>
                    <WidgetHost
                        key={this.state.iterationKey}
                        widgetHostParentProps={{
                            onWidgetLoaded: this.onWidgetLoaded,
                            onCopyWidget: allowPinning ? this.onCopyWidget : undefined
                        }}
                        convertToPixelSize={this.getWidgetPixelSize}
                        widgetPresentationMode={WidgetPresentationMode.embeddedView}
                        parentPageName={this.props.parentPageName}
                        widgetProps={widgetProps}
                        shouldNotShowTitle={true}
                        userCanConfigure={true}
                        openLinksInNewTab={true}
                        showLoadAnimations={this.state.iterationKey === 0}
                        teamContext={this.props.teamContext}
                    />
                </div>
            </>
        );
    }

    private onCopyWidget = (widgetCopyInfo: WidgetCopyProps) => {
        copyWidget(this.context.pageContext, widgetCopyInfo);
    };

    private onWidgetLoaded = () => {
        this.setState({ isLoading: false });
    };

    private getWidgetPixelSize = (): WidgetSize => {
        return this.props.renderSize;
    };

    private assembleWidgetProps(widgetSettings: ReportWidgetHostProps): DashboardWidgetProps {
        return {
            contributionId: widgetSettings.contributionId,
            name: widgetSettings.name,

            size: this.props.widgetPinningSize,
            settings: widgetSettings.settings,
            // Use custom setting, or apply smart default
            settingsVersion: widgetSettings.settingsVersion || { major: 1, minor: 0, patch: 0 },

            // These should be optional for embed scenarios
            isEnabled: true,
            position: {
                column: 0,
                row: 0
            },
            areSettingsBlockedForUser: false,
            configurationContributionId: "FAKE - UNUSED",
            isNameConfigurable: false
        };
    }
}
